import React from "react";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "./dialog";
import { Button } from "./button";
import { Typography } from "./typography";
import { FaTimes } from "react-icons/fa";

export interface BaseModalAction {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface BaseModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;

  // Header
  /** Icon displayed in the header */
  icon?: React.ReactNode;
  /** Main title of the modal */
  title: string;
  /** Subtitle displayed below the title */
  subtitle?: string;
  /** Visual variant of the header */
  variant?: "default" | "accent" | "danger";

  // Footer
  /** Primary action button configuration */
  primaryAction?: BaseModalAction;
  /** Secondary action button (defaults to Cancel) */
  secondaryAction?: BaseModalAction;
  /** Hide the footer entirely */
  hideFooter?: boolean;
  /** Custom footer content (replaces default buttons) */
  customFooter?: React.ReactNode;

  // Dialog props
  /** Size of the modal */
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  /** Additional class name for the dialog */
  className?: string;
  /** Modal content */
  children: React.ReactNode;
}

/**
 * BaseModal - Standardized modal component
 *
 * Provides a consistent structure for all modals in the application:
 * - Header with icon, title, and optional subtitle
 * - Scrollable body content
 * - Footer with primary/secondary actions
 *
 * @example
 * ```tsx
 * <BaseModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   icon={<FaUser />}
 *   title="Create User"
 *   subtitle="Fill in the details below"
 *   primaryAction={{
 *     label: "Create",
 *     onClick: handleCreate,
 *     loading: isCreating,
 *   }}
 * >
 *   <form>...</form>
 * </BaseModal>
 * ```
 */
const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  icon,
  title,
  subtitle,
  variant = "default",
  primaryAction,
  secondaryAction,
  hideFooter = false,
  customFooter,
  size = "md",
  className = "",
  children,
}) => {
  // Variant styles for the header
  const headerStyles = {
    default: "",
    accent: "bg-accent text-white",
    danger: "",
  };

  // Icon wrapper styles based on variant
  const iconWrapperStyles = {
    default: "p-2 bg-accent/10 rounded-arc-md",
    accent: "", // No wrapper for accent variant
    danger: "p-2 bg-error/10 rounded-arc-md",
  };

  // Icon color styles
  const iconColorStyles = {
    default: "text-accent",
    accent: "text-white",
    danger: "text-error",
  };

  // Text color styles for title
  const titleColorStyles = {
    default: "text-content-primary",
    accent: "text-white",
    danger: "text-content-primary",
  };

  // Text color styles for subtitle
  const subtitleColorStyles = {
    default: "text-content-secondary",
    accent: "text-white/80",
    danger: "text-content-secondary",
  };

  const renderHeader = () => (
    <DialogHeader onClose={onClose} className={headerStyles[variant]}>
      <div className="flex items-center gap-3">
        {icon && (
          variant === "accent" ? (
            <div className={`text-2xl ${iconColorStyles[variant]}`}>
              {icon}
            </div>
          ) : (
            <div className={iconWrapperStyles[variant]}>
              <div className={`text-xl ${iconColorStyles[variant]}`}>
                {icon}
              </div>
            </div>
          )
        )}
        <div>
          <Typography
            variant={variant === "accent" ? "h4" : "h6"}
            className={`font-semibold ${titleColorStyles[variant]}`}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="text-sm"
              className={subtitleColorStyles[variant]}
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </div>
    </DialogHeader>
  );

  const renderFooter = () => {
    if (hideFooter) return null;

    if (customFooter) {
      return <DialogFooter>{customFooter}</DialogFooter>;
    }

    const defaultSecondaryAction: BaseModalAction = {
      label: "Annuler",
      onClick: onClose,
    };

    const secondary = secondaryAction || defaultSecondaryAction;

    return (
      <DialogFooter>
        <div className="flex justify-between items-center w-full">
          <div />
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={secondary.onClick}
              disabled={secondary.disabled}
              leftIcon={secondary.icon || <FaTimes className="text-sm" />}
            >
              {secondary.label}
            </Button>
            {primaryAction && (
              <Button
                variant={variant === "danger" ? "danger" : "primary"}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                loading={primaryAction.loading}
                leftIcon={primaryAction.icon}
              >
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </DialogFooter>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} size={size} className={className}>
      {renderHeader()}
      <DialogBody className="max-h-[70vh] overflow-y-auto">
        {children}
      </DialogBody>
      {renderFooter()}
    </Dialog>
  );
};

BaseModal.displayName = "BaseModal";

export { BaseModal };
export default BaseModal;
